// GPU PCI device ID lookup table (decimal vendorId:deviceId → friendly name)
// Vendors: NVIDIA=4318(0x10DE), AMD=4098(0x1002), Intel=32902(0x8086), Apple=4203(0x106B)
//
// To add a new GPU: convert hex device ID to decimal, e.g. 0x2684 = 9860
// Verify at https://devicehunt.com/view/type/pci/vendor/10DE/device/XXXX
const PCI_GPU_MAP = {
  // Apple (vendor 0x106B = 4203) — integrated GPU on Apple Silicon
  '4203:0':     'Apple GPU',

  // NVIDIA (vendor 0x10DE = 4318) — verified from database + devicehunt.com
  '4318:9860':  'RTX 4090',       // 0x2684 — AD102
  '4318:9862':  'RTX 4080',       // 0x2686 — AD102
  '4318:9905':  'RTX 6000 Ada',   // 0x26B1 — AD102GL
  '4318:8707':  'RTX 3090 Ti',    // 0x2203 — GA102
  '4318:8708':  'RTX 3090',       // 0x2204 — GA102
  '4318:8710':  'RTX 3080 Ti',    // 0x2206 — GA102
  '4318:8704':  'RTX 3090',       // 0x2200 — GA102 variant
  '4318:8966':  'RTX 3080',       // 0x2206 variant
  '4318:9474':  'RTX 3060 Ti',    // 0x2504 — GA106 variant
  '4318:9478':  'RTX 3060',       // 0x2508 — GA106
  '4318:9730':  'RTX 3050',       // 0x2600 — GA107
  '4318:10114': 'RTX 4090',       // 0x2782 — AD102 variant
  '4318:10118': 'RTX 4080',       // 0x2786 — AD103
  '4318:10120': 'RTX 4070 Ti Super', // 0x2788 — AD103
  '4318:10121': 'RTX 4070 Ti',    // 0x2789 — AD104
  '4318:10370': 'RTX 4090',       // 0x2882 — AD102 variant
  '4318:10504': 'RTX 4060 Ti',    // 0x2908 — AD106
  '4318:10505': 'RTX 4060',       // 0x2909 — AD107

  // AMD (vendor 0x1002 = 4098)
  '4098:5688':  'Radeon Vega (iGPU)', // 0x1638 — Cezanne integrated
  '4098:5700':  'Radeon Vega (iGPU)', // 0x1644 — Renoir integrated
  '4098:29476': 'RX 7900 XTX',    // 0x7324 — Navi 31
  '4098:29472': 'RX 7900 XT',     // 0x7320 — Navi 31
  '4098:29952': 'RX 7800 XT',     // 0x7500 — Navi 32
  '4098:29968': 'RX 7700 XT',     // 0x7510 — Navi 32
  '4098:29316': 'RX 6900 XT',     // 0x7284 — Navi 21
  '4098:29308': 'RX 6800 XT',     // 0x727C — Navi 21
  '4098:29580': 'RX 6700 XT',     // 0x738C — Navi 22

  // Intel (vendor 0x8086 = 32902)
  '32902:18048': 'UHD 630',       // 0x4680
  '32902:22624': 'Iris Xe',       // 0x5860
  '32902:22176': 'Arc A770',      // 0x56A0
  '32902:22177': 'Arc A750',      // 0x56A1
};

/**
 * Normalize raw GPU model strings to friendly short names.
 * Handles:
 *  - Verbose vendor strings: "NVIDIA GeForce RTX 3090" → "RTX 3090"
 *  - PCI ID fallback strings: "GPU 4318:9860" → lookup → "RTX 4090"
 *  - Unknown PCI IDs: "GPU 9999:1234" → "Unknown GPU"
 */
export function friendlyGpuName(raw) {
  if (!raw) return '-';
  const s = raw.trim();

  // Handle PCI ID format: "GPU vendorId:deviceId" (decimal)
  const pciMatch = s.match(/^GPU\s+(\d+):(\d+)$/i);
  if (pciMatch) {
    const key = `${pciMatch[1]}:${pciMatch[2]}`;
    return PCI_GPU_MAP[key] || raw;
  }

  // Strip common vendor prefixes
  let name = s;
  name = name.replace(/^NVIDIA\s+GeForce\s+/i, '');
  name = name.replace(/^NVIDIA\s+Corporation\s+/i, '');
  name = name.replace(/^NVIDIA\s+/i, '');
  name = name.replace(/^GeForce\s+/i, '');
  name = name.replace(/^AMD\s+Radeon\s+(RX|Pro|Vega)/i, '$1');
  name = name.replace(/^AMD\s+Radeon\s+/i, '');
  name = name.replace(/^AMD\s+/i, '');
  name = name.replace(/^Intel\(R\)\s+/i, '');
  name = name.replace(/^Intel\s+/i, '');
  name = name.replace(/^Apple\s+/i, '');

  // Clean up extra whitespace
  name = name.replace(/\s+/g, ' ').trim();

  return name || raw;
}
